import { useMemo, useState } from "react";
import { type Path, useFormContext, useWatch } from "react-hook-form";
import { useParams } from "react-router-dom";
import type { BidPostData } from "validations/bidValidation";

import { useDebounce } from "@krea/shared/utils/use-debounce";

import { useSelectedBidIndex } from "@/components/lender-application-details/bids/SelectedBidIndexContext";
import {
  BidTypeEnum,
  OfferAttractivenessRecommendationStatusEnum,
} from "@/features/common/enums";
import { useGetCurrentLenderCompanyQuery } from "@/features/lender-companies/hooks/use-get-lender-company.query";
import { useGetOfferAttractiveness } from "@/features/lender-responses/hooks/data/use-get-offer-attractiveness.query";

import { InterestRateExplanationModal } from "./interest-rate-explanation-modal";
import { KeepOfferAttractivenessModal } from "./keep-offer-attractiveness-modal";
import { OfferAttractivenessCard } from "./offer-attractiveness-card";
import { OfferAttractivenessLocked } from "./offer-attractiveness-locked";
import { OfferAttractivenessTrialStrip } from "./offer-attractiveness-trial-strip";
import { getOfferAttractivenessTrialState } from "./trial";

function getFieldNameForRecommendation(
  status: OfferAttractivenessRecommendationStatusEnum,
  bidType: BidTypeEnum | undefined,
  selectedBidIndex: number,
): Path<BidPostData> | null {
  switch (status) {
    case OfferAttractivenessRecommendationStatusEnum.LOWER_COMPARABLE_INTEREST_RATE:
      return null;
    case OfferAttractivenessRecommendationStatusEnum.INCREASE_AMOUNT:
      return bidType === BidTypeEnum.FACTORING
        ? (`bids.${selectedBidIndex}.purchaseLimit` as Path<BidPostData>)
        : (`bids.${selectedBidIndex}.amount` as Path<BidPostData>);
    case OfferAttractivenessRecommendationStatusEnum.INCREASE_REPAYMENT_TIME:
      if (bidType === BidTypeEnum.REVOLVING_CREDIT) {
        return `bids.${selectedBidIndex}.monthlyAmortisation` as Path<BidPostData>;
      }

      return `bids.${selectedBidIndex}.paybackTimeMonths` as Path<BidPostData>;
    default:
      return null;
  }
}

interface OfferAttractivenessProps {
  lenderApplicationId: number;
  isFeatureDisabled?: boolean;
}

const OfferAttractiveness = ({
  lenderApplicationId,
  isFeatureDisabled = false,
}: OfferAttractivenessProps) => {
  const [interestRateModalOpen, setInterestRateModalOpen] = useState(false);
  const [keepFeatureModalOpen, setKeepFeatureModalOpen] = useState(false);

  const {
    formState: { isValid, isDirty },
    control,
    setFocus,
  } = useFormContext<BidPostData>();

  const selectedBidIndex = useSelectedBidIndex();
  const bids = useWatch({ control, name: "bids" });
  const bidType = bids[selectedBidIndex]?.type as BidTypeEnum | undefined;

  const handleRecommendationClick = (
    status: OfferAttractivenessRecommendationStatusEnum,
  ) => {
    if (
      status ===
      OfferAttractivenessRecommendationStatusEnum.LOWER_COMPARABLE_INTEREST_RATE
    ) {
      setInterestRateModalOpen(true);

      return;
    }

    const fieldName = getFieldNameForRecommendation(
      status,
      bidType,
      selectedBidIndex,
    );

    if (fieldName) {
      setFocus(fieldName);
    }
  };

  const debouncedData = useDebounce(
    useMemo(
      () => ({ bidData: { bids }, isDirty, isValid }),
      [bids, isDirty, isValid],
    ),
    2000,
  );

  const offerAttractivenessFormData = useMemo((): BidPostData => {
    return {
      bids: debouncedData.bidData.bids,
      fieldRequests: [],
    };
  }, [debouncedData]);

  const { lenderCompanyId } = useParams<{ lenderCompanyId: string }>();
  const isQueryEnabled =
    !isFeatureDisabled &&
    offerAttractivenessFormData.bids.length > 0 &&
    debouncedData.isValid &&
    debouncedData.isDirty;

  const { data } = useGetOfferAttractiveness(
    {
      lenderCompanyId: Number(lenderCompanyId),
      lenderApplicationId,
      input: offerAttractivenessFormData,
    },
    { enabled: isQueryEnabled },
  );

  // Cached by React Query — LenderResponseForm already holds this, so no extra request.
  const { data: lenderCompany } = useGetCurrentLenderCompanyQuery();
  const trial = getOfferAttractivenessTrialState(
    lenderCompany?.isOfferAttractivenessTrialActive,
    lenderCompany?.offerAttractivenessTrialEndsOn,
  );

  const card = (
    <OfferAttractivenessCard
      score={isFeatureDisabled ? null : (data?.score ?? null)}
      recommendations={isFeatureDisabled ? [] : (data?.recommendations ?? [])}
      bidType={bidType}
      onRecommendationClick={handleRecommendationClick}
    />
  );

  if (isFeatureDisabled) {
    return <OfferAttractivenessLocked />;
  }

  return (
    <>
      {trial.isActive && (
        <OfferAttractivenessTrialStrip
          trial={trial}
          onKeepClick={() => setKeepFeatureModalOpen(true)}
        />
      )}
      {card}
      <InterestRateExplanationModal
        open={interestRateModalOpen}
        setOpen={setInterestRateModalOpen}
      />
      <KeepOfferAttractivenessModal
        open={keepFeatureModalOpen}
        setOpen={setKeepFeatureModalOpen}
        trial={trial}
        countryCode={lenderCompany?.countryCode}
      />
    </>
  );
};

export { OfferAttractiveness };
